import React, { useCallback, useEffect, useState } from 'react';
import { fetchCustomerReleaseFeed, type CustomerReleaseFeedResponse, type ReleaseNotesEntry } from '@circuitry-ai/doc-data';
import { ReleaseNotesModal } from './ReleaseNotesModal';
import { downloadReleaseNotesWord, printReleaseNotesPdf } from '../utils/releaseNotesExportUtils';
import { CtyShowToast, checkType } from '../toast/CtyToast';

/** Release notes load in pages of this size, newest-first, via infinite scroll. */
const PAGE_SIZE = 20;

export interface ReleaseNotesFooterLinkProps {
  /** IDP tenant id for the customer-feed lookup. When absent, the fallback renders unconditionally. */
  readonly tenantId?: string;
  /** IDP customer slug for the customer-feed lookup (e.g. "cars"). */
  readonly customer?: string;
  /** Bearer token used to authenticate against the IDP customer-feed API. */
  readonly token?: string;
  /** Overrides `CTY_IDP_API_URL` for this call. */
  readonly baseUrl?: string;
  /** Rendered as-is (unchanged) whenever the customer-feed request fails, is unconfigured, or returns no releases. */
  readonly fallback: React.ReactNode;
}

function pickLatestRelease(releases: ReleaseNotesEntry[]): ReleaseNotesEntry | undefined {
  return releases.reduce<ReleaseNotesEntry | undefined>((latest, release) => {
    if (!latest) return release;
    return new Date(release.signed_off_at).getTime() > new Date(latest.signed_off_at).getTime() ? release : latest;
  }, undefined);
}

/**
 * Fetches the IDP customer-feed release notes. Renders a clickable version link that opens the
 * release notes screen when releases are available; renders `fallback` unchanged otherwise, so
 * an existing build-number display keeps working when the feed is unconfigured or unreachable.
 */
export function ReleaseNotesFooterLink(props: ReleaseNotesFooterLinkProps) {
  const { tenantId, customer, token, baseUrl, fallback } = props;
  const [feed, setFeed] = useState<CustomerReleaseFeedResponse | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!tenantId || !customer) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();

    fetchCustomerReleaseFeed({ tenantId, customer, token, baseUrl, limit: PAGE_SIZE, offset: 0, signal: controller.signal })
      .then((response) => {
        if (!cancelled && response?.releases?.length) {
          setFeed(response);
          setHasMore(!!response.has_more);
        }
      })
      .catch((error) => {
        // Network error, 401/404, or misconfigured base URL — keep showing `fallback`.
        if (!cancelled) {
          console.warn('[ReleaseNotesFooterLink] customer-feed request failed, showing fallback:', error);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [tenantId, customer, token, baseUrl]);

  // Infinite scroll: ReleaseNotesModal calls this when the release list is scrolled near its
  // bottom. Appends the next page, de-duplicated by id, onto the already-loaded releases.
  const handleLoadMore = useCallback(() => {
    if (!tenantId || !customer || isLoadingMore || !hasMore) {
      return;
    }
    const nextOffset = feed?.releases.length ?? 0;
    setIsLoadingMore(true);

    fetchCustomerReleaseFeed({ tenantId, customer, token, baseUrl, limit: PAGE_SIZE, offset: nextOffset })
      .then((response) => {
        setFeed((prev) => {
          if (!prev) return response;
          const seen = new Set(prev.releases.map((release) => release.id));
          return { ...response, releases: [...prev.releases, ...response.releases.filter((release) => !seen.has(release.id))] };
        });
        setHasMore(!!response.has_more);
      })
      .catch((error) => {
        console.warn('[ReleaseNotesFooterLink] failed to load more releases:', error);
      })
      .finally(() => setIsLoadingMore(false));
  }, [tenantId, customer, token, baseUrl, feed, hasMore, isLoadingMore]);

  const latestRelease = feed ? pickLatestRelease(feed.releases) : undefined;

  const handleDownloadWord = async (release: ReleaseNotesEntry) => {
    try {
      await downloadReleaseNotesWord(release, token, feed?.customer, baseUrl);
      CtyShowToast(checkType.success, 'Word document downloaded.');
    } catch (error) {
      CtyShowToast(checkType.error, 'Word export failed.');
      console.error('[ReleaseNotesFooterLink] Word export failed:', error);
    }
  };

  const handleDownloadPdf = (release: ReleaseNotesEntry) => {
    printReleaseNotesPdf(release);
  };

  if (!feed || !latestRelease) {
    return <>{fallback}</>;
  }

  return (
    <>
      <button type="button" className="underline" onClick={() => setIsModalOpen(true)}>
        V {latestRelease.name}
      </button>
      <ReleaseNotesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customer={feed.customer}
        releases={feed.releases}
        total={feed.total}
        initialReleaseId={latestRelease.id}
        onDownloadWord={handleDownloadWord}
        onDownloadPdf={handleDownloadPdf}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
        tenantId={tenantId}
        subscribersToken={token}
        subscribersBaseUrl={baseUrl}
      />
    </>
  );
}

export default ReleaseNotesFooterLink;
